import { Injectable } from '@angular/core';
import { PATHS } from '@shared/constants/paths';
import { NavigationHelperService } from '@shared/helpers/navigation.helpers.service';
import {
  firstValueFrom,
  map,
  Observable,
  switchMap,
  shareReplay,
  combineLatest,
  distinctUntilChanged,
  of,
  tap,
} from 'rxjs';
import { Store } from '@ngrx/store';
import {
  getMPPBaseViewModel,
  MPP_ACTIONS,
  RECIPE_ACTIONS,
  getAllRecipes,
  LastVisited,
  ANALYTICS_ACTIONS,
  MPPAnalyticsConstants,
  pageLoadTransformer,
  buttonClickTransformer,
  selectRecipeImageSrc,
  selectLastRecipeCustomer,
  getWatermark,
  MPP_PRODUCT_ACTIONS,
} from '@usf/ngrx-tools';
import { mppViewModel } from '@usf/ngrx-tools/lib/model/mpp-view-model';
import { MPPTracingConstants } from '@usf/ngrx-tools';
import { baseMPPTransform } from '@usf/ngrx-tools';
import { recipeMPPViewModel } from '../model/recipe-mpp-view-model';
import { RecipeViewModelSelectors } from '../pages/recipes/selectors/recipe-view-model-selectors';
import { FormGroup } from '@angular/forms';
import { createMppData } from '../model/create-mpp-model';
import { RecipeService } from '../pages/recipes/services/recipe.service';
import { PlatformService } from '@panamax/app-state';
import { RecipeImageService } from '../shared/services/recipe-image/recipe-image.service';
import { selectedCustomer } from '@app/ngrx-customer/store';
export const selectRecipes = (state: any) => state.recipes;
export const selectValidators = (state: any) => state.validators;
@Injectable({
  providedIn: 'root',
})
export class MppService {
  public page: string;
  public createMppForm: FormGroup;
  createMppModalIsOpen = false;

  createMppDataModalData: createMppData;

  private mppPageViewModel$ = this.store.select(getMPPBaseViewModel).pipe(
    map(baseViewModel => {
      return baseViewModel as mppViewModel;
    }),
  );

  private recipeMPPViewModel$ = this.store.select(
    this.recipeViewModelSelectors.recipeMPPViewModel$,
  );

  constructor(
    private navigationHelperService: NavigationHelperService,
    private platformService: PlatformService,
    private recipeViewModelSelectors: RecipeViewModelSelectors,
    private recipeService: RecipeService,
    readonly store: Store,
  ) {}

  public get mppPageViewModel(): Observable<mppViewModel> {
    return this.mppPageViewModel$;
  }

  public get recipeMPPViewModel(): Observable<recipeMPPViewModel> {
    return combineLatest([
      this.recipeMPPViewModel$,
      this.store.select(selectRecipeImageSrc()),
    ]).pipe(
      map(([viewModel, imageSrcs]) => {
        const recipes = viewModel.recipes.map(card => {
          const src = imageSrcs[card.recipeId]?.src;
          // want to avoid null
          return {
            ...card,
            imageSrc: src ?? undefined,
          };
        });
        return {
          ...viewModel,
          recipes,
        };
      }),
      distinctUntilChanged(
        (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  enterLanding() {
    this.store.dispatch(
      MPP_ACTIONS.enterLanding({
        tracking: {
          tracing: {
            data: {
              traceContext: MPPTracingConstants.enterDashboard,
              isEndOfTrace: true,
              isStartOfTrace: true,
              attributes: {
                event: MPPTracingConstants.enterDashboardEvent,
                spanName: MPPTracingConstants.enterDashboardSpan,
              },
            },
            transformFunc: baseMPPTransform,
          },
        },
      }),
    );
    this.store.dispatch(
      ANALYTICS_ACTIONS.enterLanding({
        tracking: {
          analytics: {
            data: {
              pageName: MPPAnalyticsConstants.dashboardPage,
              event: MPPAnalyticsConstants.pageLoaded,
              capability: MPPAnalyticsConstants.capability,
            },
            transformFunc: pageLoadTransformer,
          },
        },
      }),
    );
  }

  async enterRecipe() {
    const currentCustomer = await firstValueFrom(
      this.store.select(selectedCustomer),
    );
    const lastRecipeCustomer = await firstValueFrom(
      this.store.select(selectLastRecipeCustomer()),
    );
    this.store.dispatch(
      MPP_ACTIONS.enterRecipe({
        tracking: {
          tracing: {
            data: {
              traceContext: MPPTracingConstants.enterRecipesPage,
              isEndOfTrace: true,
              isStartOfTrace: true,
              attributes: {
                event: MPPTracingConstants.enterRecipesPageEvent,
                spanName: MPPTracingConstants.enterRecipesPageSpan,
              },
            },
            transformFunc: baseMPPTransform,
          },
        },
      }),
    );
    this.store.dispatch(
      ANALYTICS_ACTIONS.enterLanding({
        tracking: {
          analytics: {
            data: {
              pageName: MPPAnalyticsConstants.recipePage,
              event: MPPAnalyticsConstants.pageLoaded,
              capability: MPPAnalyticsConstants.capability,
            },
            transformFunc: pageLoadTransformer,
          },
        },
      }),
    );
    if (currentCustomer.customerNumber !== lastRecipeCustomer) {
      this.store.dispatch(MPP_PRODUCT_ACTIONS.getMppProducts());
      this.store.dispatch(RECIPE_ACTIONS.resetRecipes());
      this.store.dispatch(
        MPP_ACTIONS.getRecipes({
          tracking: {
            tracing: {
              data: {
                traceContext: MPPTracingConstants.getRecipes,
                isEndOfTrace: false,
                isStartOfTrace: true,
                attributes: {
                  event: MPPTracingConstants.getRecipesEvent,
                  spanName: MPPTracingConstants.getRecipesSpan,
                },
              },
              transformFunc: baseMPPTransform,
            },
          },
        }),
      );
      this.store.dispatch(
        RECIPE_ACTIONS.getRecipes({
          customerNumber: (await currentCustomer).customerNumber,
        }),
      );
    } else {
      const watermark = await firstValueFrom(this.store.select(getWatermark()));
      this.store.dispatch(
        MPP_ACTIONS.getMyChanges({
          tracking: {
            tracing: {
              data: {
                traceContext: MPPTracingConstants.getMyChanges,
                isEndOfTrace: false,
                isStartOfTrace: true,
                attributes: {
                  event: MPPTracingConstants.getMyChangesEvent,
                  spanName: MPPTracingConstants.getMyChangesEvent,
                },
              },
              transformFunc: baseMPPTransform,
            },
          },
        }),
      );
      this.store.dispatch(
        RECIPE_ACTIONS.getMyChanges({ timestamp: watermark }),
      );
    }
    this.store.dispatch(RECIPE_ACTIONS.resetRecipesSort());
  }

  clickMppEntryPoint() {
    this.store.dispatch(
      MPP_ACTIONS.clickMPPEntryPoint({
        tracking: {
          tracing: {
            data: {
              traceContext: MPPTracingConstants.clickMPPEntryPoint,
              isEndOfTrace: true,
              isStartOfTrace: true,
              attributes: {
                event: MPPTracingConstants.clickMPPEntryPointEvent,
                spanName: MPPTracingConstants.clickMPPEntryPointSpan,
              },
            },
            transformFunc: baseMPPTransform,
          },
        },
      }),
    );
    this.store.dispatch(
      ANALYTICS_ACTIONS.enterLanding({
        tracking: {
          analytics: {
            data: {
              buttonName: MPPAnalyticsConstants.buttonNameMPP,
              event: MPPAnalyticsConstants.buttonClick,
              capability: MPPAnalyticsConstants.capability,
            },
            transformFunc: buttonClickTransformer,
          },
        },
      }),
    );
  }

  goToPageWithinMPP(link: string) {
    if (!!link) {
      this.navigationHelperService.routeTo(PATHS.MPP + '/' + link);
    } else {
      this.navigationHelperService.routeTo(PATHS.MPP);
    }
  }

  goToDetails(recipeId) {
    this.navigationHelperService.routeTo(
      PATHS.MPP + '/recipe/details/' + recipeId,
    );
  }

  goToIngredients(recipeId) {
    this.navigationHelperService.routeTo(
      PATHS.MPP + '/recipe/details/' + recipeId + '/ingredients',
    );
  }

  gotToProductSearch(searchKey: string) {
    this.navigationHelperService.mppToNewSearch(searchKey);
  }

  lastVisitedPage(lastVisited: LastVisited) {
    this.store.dispatch(MPP_ACTIONS.visitingMPPRootPage({ lastVisited }));
  }

  handleCreateMppDismiss() {
    this.createMppModalIsOpen = false;
  }
  openCreatePopover() {
    this.createMppModalIsOpen = true;
  }
  handleCreateMppSubmission(createMppData: createMppData) {
    if (createMppData.type === 'recipe') {
      this.recipeService.createRecipe(createMppData);
    }
    this.handleCreateMppDismiss();
  }
  async doesMppNameExists(name: String, type: string): Promise<boolean> {
    if (type === 'recipe') {
      const recipes = await firstValueFrom(this.store.select(getAllRecipes()));
      const recipeNames: string[] = [];
      recipes?.forEach(recipe => {
        recipeNames.push(recipe.recipeName.toLowerCase());
      });
      return recipeNames.includes(name.toLowerCase());
    }
  }
}
